import { useAction, useAtom } from '@reatom/npm-react'
import BigNumber from 'bignumber.js'
import styled from 'styled-components'
import { match } from 'ts-pattern'

import { AsyncStateN } from '@blade/std-lib'
import { CircularLoader, Numeric, Skeleton } from '@blade/ui-kit'
import { CurrencyModel } from '@root/entities/currency'
import { NetworkIcon } from '@root/entities/network/ui'
import { NotEnoughLiquidityError } from '@root/entities/quote/atoms'
import {
  activePairApproveAllowanceStateAtom,
  activeTradingPairAllowanceAtom,
  approveActivePairAllowanceAction,
  loadActivePairAllowanceAction,
} from '@root/features/trade/trading/atoms/reatom-trading-allowance'
import {
  environmentAtom,
  isTauriOrBrowserLocalAtom,
  PlatformUtil,
  type Error,
} from '@root/shared'
import { TgUtil } from '@root/shared/lib/telegram'
import { useTranslation } from '@root/shared/localization'

import { useTradingControlsValidation } from '../../common/trading-controls-validation'

import type { PairModel } from '@root/entities/pair'
import type { Quote } from '@root/entities/quote'
import type {
  InsufficientBalanceError,
  NoAmountError,
} from '@root/features/trade/validation-errors'
import type { ApiError } from '@root/shared/api'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  variant: 'buy' | 'sell'
  onClick?: () => void
  pair: PairModel
  quoteState: AsyncStateN.AsyncStateN<
    Error.UnknownError | ApiError.ApiError | NotEnoughLiquidityError,
    Quote.Quote
  >
  requestState: AsyncStateN.AsyncStateN<Error.UnknownError, unknown>
  onQuoteRetry?: () => void
  validation: InsufficientBalanceError | NoAmountError | undefined
  amount: BigNumber

  currencyAmount: BigNumber | undefined
  baseAmount: BigNumber | undefined
} & ClassNameProps

export const TradingControlsButtonBase: React.FC<Props> = ({
  className,
  variant,
  onClick,
  pair,
  validation,
  currencyAmount,
  baseAmount,
  quoteState,
  onQuoteRetry,
}) => {
  const { t } = useTranslation()
  const { validationMessage } = useTradingControlsValidation(validation)
  const title = variant === 'buy' ? t('trading.buy') : t('trading.sell')
  const [allowanceApprovedState] = useAtom(activeTradingPairAllowanceAtom)
  const checkApproval = useAction(loadActivePairAllowanceAction)
  const [isTauriOrBrowserLocal] = useAtom(isTauriOrBrowserLocalAtom)
  const [noLiquidity] = useAtom((ctx) => {
    const hasLiquidityRemovedTx = ctx.spy(pair.hasLiquidityRemovedTx)
    if (hasLiquidityRemovedTx) {
      return true
    }
    const liquidity = ctx
      .spy(pair.liquidityUsd)
      .integerValue(BigNumber.ROUND_DOWN)
    return liquidity.isZero()
  })

  const [approveRequestState] = useAtom(activePairApproveAllowanceStateAtom)
  const approve = useAction(approveActivePairAllowanceAction)

  const [environment] = useAtom(environmentAtom)

  const renderTitle = () => {
    const amount = currencyAmount

    return (
      <ButtonLabel>
        {title}
        {amount ? (
          <>
            <span> </span>
            <Numeric
              prefix={variant === 'buy' ? '~' : ''}
              value={amount}
              preset="currency-amount-short"
              currency={pair.quote.ticker}
            />
          </>
        ) : undefined}
      </ButtonLabel>
    )
  }

  const renderBaseAmount = () => {
    if (AsyncStateN.isLoading(quoteState) || AsyncStateN.isError(quoteState)) {
      return <Skeleton width={50} height={16} />
    }

    if (AsyncStateN.isNotInitiated(quoteState)) {
      return undefined
    }

    if (!baseAmount || !currencyAmount) {
      return undefined
    }

    const amount = match(variant)
      .with('buy', () => baseAmount.multipliedBy(-1))
      .with('sell', () => baseAmount)
      .exhaustive()

    return (
      <>
        <Numeric
          prefix={variant === 'sell' ? '~' : ''}
          value={amount}
          preset="currency-amount-short"
        />
        {CurrencyModel.isNativeOrWrapped(pair.base.network)(pair.base) ? (
          <NetworkIcon network={pair.base.network} size={14} />
        ) : (
          pair.base.ticker
        )}
      </>
    )
  }

  const baseProps = {
    $variant: variant,
    className: className,
    'data-type': variant,
  }

  const renderButtonContent = (options?: {
    // TODO: Revise this option with now added quote
    skipInsufficientBalanceError?: boolean
  }) => {
    const { skipInsufficientBalanceError = false } = options ?? {}

    if (AsyncStateN.isLoading(allowanceApprovedState)) {
      return (
        <>
          {t('trading.checking-approval')}
          <CircularLoader size={16} />
        </>
      )
    }

    if (AsyncStateN.isError(allowanceApprovedState)) {
      return <>{t('trading.check-approval')}</>
    }

    if (
      AsyncStateN.isSuccess(allowanceApprovedState) &&
      !allowanceApprovedState.value.tokens.every((it) => it.isApproved)
    ) {
      if (AsyncStateN.isLoading(approveRequestState)) {
        return (
          <>
            {t('trading.approving-tokens')}
            <CircularLoader size={16} />
          </>
        )
      }

      return <>{t('trading.approve-tokens')}</>
    }

    if (
      validation &&
      !(
        validation._tag === 'InsufficientBalance' &&
        skipInsufficientBalanceError
      ) &&
      validationMessage
    ) {
      return <>{validationMessage}</>
    }

    if (AsyncStateN.isLoading(quoteState)) {
      return (
        <>
          {t('trading.quote-pending')}
          <CircularLoader size={16} />
        </>
      )
    }

    if (AsyncStateN.isError(quoteState)) {
      return <>{t('trading.quote-retry')}</>
    }

    return (
      <>
        {renderTitle()}
        <NativeAmount>{renderBaseAmount()}</NativeAmount>
      </>
    )
  }

  /*
   * On the web this button has never executed a swap, and it should stop
   * pretending the reason is a missing download.
   *
   * It used to open the download dialog, which in both deployed builds has a
   * Download button pointing at `undefined` - VITE_DOWNLOAD_URL is set in
   * neither image - and an Open in the App button deep-linking to a desktop
   * build nobody publishes for kol.trade. So the honest destination is the
   * bot, which is where a trade can actually be placed today. The quote and
   * the amounts stay on the button, because they are true either way and are
   * the reason somebody is looking at it.
   */
  if (!isTauriOrBrowserLocal) {
    return (
      <Root
        type="button"
        {...baseProps}
        onClick={() => {
          PlatformUtil.openLink(
            TgUtil.createTelegramLink(environment.buybotUsername),
          )
        }}
      >
        {renderButtonContent({ skipInsufficientBalanceError: true })}
      </Root>
    )
  }

  const showNoLiquidity =
    noLiquidity ||
    (AsyncStateN.isError(quoteState) &&
      quoteState.error instanceof NotEnoughLiquidityError)

  if (showNoLiquidity) {
    return (
      <Root type="button" disabled {...baseProps}>
        {t('trading.quote.not-enough-liquidity')}
      </Root>
    )
  }

  if (AsyncStateN.isLoading(allowanceApprovedState)) {
    return (
      <Root type="button" disabled {...baseProps}>
        {renderButtonContent()}
      </Root>
    )
  }

  if (AsyncStateN.isError(allowanceApprovedState)) {
    return (
      <Root
        disabled={false}
        type="button"
        {...baseProps}
        onClick={() => {
          checkApproval()
        }}
      >
        {renderButtonContent()}
      </Root>
    )
  }

  if (
    AsyncStateN.isSuccess(allowanceApprovedState) &&
    !allowanceApprovedState.value.tokens.every((it) => it.isApproved)
  ) {
    if (AsyncStateN.isLoading(approveRequestState)) {
      return (
        <Root type="button" disabled {...baseProps}>
          {renderButtonContent()}
        </Root>
      )
    }

    return (
      <Root
        type="button"
        {...baseProps}
        data-type="buy"
        onClick={() => approve()}
      >
        {renderButtonContent()}
      </Root>
    )
  }

  if (validationMessage) {
    return (
      <Root type="button" disabled {...baseProps}>
        {renderButtonContent()}
      </Root>
    )
  }

  if (AsyncStateN.isLoading(quoteState)) {
    return (
      <Root type="button" disabled {...baseProps}>
        {renderButtonContent()}
      </Root>
    )
  }

  if (AsyncStateN.isError(quoteState)) {
    return (
      <Root
        type="button"
        {...baseProps}
        onClick={() => {
          onQuoteRetry?.()
        }}
      >
        {renderButtonContent()}
      </Root>
    )
  }

  return (
    <Root
      type="button"
      onClick={onClick}
      $variant={variant}
      className={className}
      data-type={variant}
    >
      {renderButtonContent()}
    </Root>
  )
}

const NativeAmount = styled.span`
  color: var(--text-12);

  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  gap: 4px;

  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
`

const Root = styled.button<{ $variant: 'buy' | 'sell' }>`
  border: 1px solid;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &[data-type='buy'] {
    border-color: var(--upA-5);
    background: var(--upA-2);
    color: var(--upAccent-9);

    &:not(:disabled):hover {
      border-color: var(--upA-6);
      background: var(--upA-3);
    }
  }

  &[data-type='sell'] {
    border-color: var(--downA-5);
    background: var(--downA-2);
    color: var(--downAccent-9);
  }

  &:disabled {
    background: var(--backgroundA-2);
    border-color: transparent;
    cursor: default;

    color: var(--textA-10);
    & ${NativeAmount} {
      color: var(--textA-8);
    }
  }

  color: var(--textA-8);

  height: 42px;
  border-radius: 10px;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;

  display: grid;
  align-items: center;
  grid-template-columns: auto auto;
  justify-content: space-between;

  gap: 8px;
  padding-inline: 10px;
`

const ButtonLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
